import { MediaType } from '../media-types';
import { isAry2 } from '../utils/common';

type ReadonlyNonEmptyArray<T> = readonly [T, ...T[]];
type ReadonlyOneOrMore<T> = T | ReadonlyNonEmptyArray<T>;

const _mediaTypeToExts: Map<string, ReadonlyNonEmptyArray<string>> = new Map();
const _extToPreferredMediaType: Map<string, string> = new Map();
// const mediaTypeToPreferred: Map<string, string> = new Map();
const _mediaTypeToOthers: Map<string, ReadonlyNonEmptyArray<string>> = new Map();
const _extToPreferredExt: Map<string, string> = new Map();

// Construct the maps
{
	// eslint-disable-next-line no-inner-declarations
	function getArray<T>(thing: ReadonlyOneOrMore<T>): ReadonlyNonEmptyArray<T> {
		if (isAry2(thing)) {
			return thing;
		} else {
			return [thing];
		}
	}

	const mediaTypesAndExts: readonly [ReadonlyOneOrMore<string>, ReadonlyOneOrMore<string>][] = [
		[[MediaType.OctetStream, MediaType.OctetStream2], 'bin'],

		[[MediaType.Woff, 'application/x-font-woff'], 'woff'],
		[[MediaType.Woff2, 'application/x-font-woff2'], 'woff2'],
		[['font/ttf', 'application/x-font-ttf', 'application/x-font-truetype'], 'ttf'],

		[MediaType.Bmp, 'bmp'],
		[MediaType.Dib, 'dib'],
		['image/gif', 'gif'],
		[
			['image/jpeg', 'image/jpg'],
			['jpg', 'jpeg', 'jpe'],
		],
		[[MediaType.Png, 'image/x-png'], 'png'],

		[
			[
				MediaType.Tiff,
				'image/tif',
				'image/x-tiff',
				'image/x-tif',
				'application/tiff',
				'application/tif',
				'application/x-tiff',
				'application/x-tif',
			],
			['tif', 'tiff'],
		],

		['image/webp', 'webp'],

		[[MediaType.Emf, 'image/x-emf'], 'emf'],
		[[MediaType.Emz, 'image/x-emz'], 'emz'],
		[[MediaType.Wmf, 'image/x-wmf'], 'wmf'],
		[[MediaType.Wmz, 'image/x-wmz'], 'wmz'],

		[MediaType.MHTML, ['mhtml', 'mht']],
		[
			[MediaType.Html, 'text/x-html'],
			['html', 'htm'],
		],
		['application/xhtml+xml', 'xhtml'],

		['text/css', 'css'],
		[[MediaType.Xml, 'text/xml'], 'xml'],

		[[MediaType.Ico, 'image/x-icon'], 'ico'],

		[MediaType.Svg, 'svg'],

		// From https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types, lol
		[
			[
				MediaType.Js,
				'application/javascript',
				'application/ecmascript',
				'application/x-ecmascript',
				'application/x-javascript',
				'text/ecmascript',
				'text/javascript1.0',
				'text/javascript1.1',
				'text/javascript1.2',
				'text/javascript1.3',
				'text/javascript1.4',
				'text/javascript1.5',
				'text/jscript',
				'text/livescript',
				'text/x-ecmascript',
				'text/x-javascript',
			],
			'js',
		],

		['application/vnd.ms-officetheme', 'thmx'],

		[MediaType.Pdf, 'pdf'],

		[['application/rtf', 'text/rtf'], 'rtf'],
		[MediaType.Rtfd, 'rtfd'],

		// 'application/msword2' is a special Tika type? We try to normalize to /msword
		[[MediaType.MsWord, 'application/msword2'], 'doc'],
		[MediaType.MsWordDocOoxml, 'docx'],
		[MediaType.MsWordTemplate, 'dotx'],
		[
			[MediaType.MsWordDocMacroEnabled, 'application/vnd.ms-word.document.macroenabled.12'],
			'docm',
		],
		[
			[
				MediaType.MsWordTemplateMacroEnabled,
				'application/vnd.ms-word.template.macroenabled.12',
			],
			'dotm',
		],

		// Spreadsheets
		['application/vnd.ms-excel', ['xls', 'xlt']],
		['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'xlsx'],
		['application/vnd.openxmlformats-officedocument.spreadsheetml.template', 'xltx'],
		[
			[
				'application/vnd.ms-excel.sheet.macroEnabled.12',
				'application/vnd.ms-excel.sheet.macroenabled.12',
			],
			'xlsm',
		],
		[
			[
				'application/vnd.ms-excel.template.macroEnabled.12',
				'application/vnd.ms-excel.template.macroenabled.12',
			],
			'xltm',
		],
		[
			[
				'application/vnd.ms-excel.addin.macroEnabled.12',
				'application/vnd.ms-excel.addin.macroenabled.12',
			],
			'xlam',
		],
		[
			[
				'application/vnd.ms-excel.sheet.binary.macroEnabled.12',
				'application/vnd.ms-excel.sheet.binary.macroenabled.12',
			],
			'xlsb',
		],
		// [MediaType.SpreadsheetMl, ['xml', 'xls']],

		// From tika
		// ['application/vnd.ms-spreadsheetml', 'xml'],

		[MediaType.CSV, 'csv'],
		[MediaType.TSV, 'tsv'],

		// Load files
		['text/x.load-file.opticon', 'opt'],
		['text/x.load-file.concordance', 'dat'],

		[[MediaType.Pages, MediaType.Pages13, MediaType.Pages18], 'pages'],
		[[MediaType.Numbers, MediaType.Numbers13, MediaType.Numbers18], 'numbers'],
		[[MediaType.Keynote, MediaType.Keynote13, MediaType.Keynote18], 'key'],

		[MediaType.Ole1, 'ole1'],
		[MediaType.Ole2, 'ole2'],
	];

	for (const [typeOrTypes, extOrExts] of mediaTypesAndExts) {
		const types = getArray(typeOrTypes);
		const exts = getArray(extOrExts);

		for (const type of types) {
			if (_mediaTypeToExts.has(type)) {
				throw new Error('Duplicate media type ' + type);
			}

			_mediaTypeToExts.set(type, exts);
			_mediaTypeToOthers.set(type, types);
			// mediaTypeToPreferred.set(type, types[0]);
		}

		for (const ext of exts) {
			if (ext.startsWith('.')) {
				throw new Error('Ext starts with "."' + ext);
			}

			if (_extToPreferredMediaType.has(ext)) {
				throw new Error('Duplicate ext name ' + ext);
			}

			_extToPreferredMediaType.set(ext, types[0]);
			_extToPreferredExt.set(ext, exts[0]);
		}
	}
}

// Export the maps, changing to readonly types
export const mediaTypeToExts: ReadonlyMap<string, ReadonlyNonEmptyArray<string>> = _mediaTypeToExts;
export const extToPreferredMediaType: ReadonlyMap<string, string> = _extToPreferredMediaType;
// const mediaTypeToPreferred: Map<string, string> = new Map();
export const mediaTypeToOthers: ReadonlyMap<
	string,
	ReadonlyNonEmptyArray<string>
> = _mediaTypeToOthers;
export const extToPreferredExt: ReadonlyMap<string, string> = _extToPreferredExt;
